import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { generateConfig } from "../../utils/api";
import { handleApiError } from "../../utils/apiErrorHandler";

export const fetchClientStatement = createAsyncThunk(
  "reports/fetchClientStatement",
  async (
    { clientId, startDate, endDate },
    { rejectWithValue, getState, dispatch }
  ) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/reports/client-statement`,
        { clientId, startDate, endDate },
        config
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchRevenueReport = createAsyncThunk(
  "reports/fetchRevenueReport",
  async ({ startDate, endDate }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/reports/revenue`,
        { startDate, endDate },
        config
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchCollectionsReport = createAsyncThunk(
  "reports/fetchCollectionsReport",
  async ({ startDate, endDate, statusFilter }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/reports/collections`,
        { startDate, endDate, statusFilter },
        config
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchClinicianHoursReport = createAsyncThunk(
  "reports/fetchClinicianHoursReport",
  async ({ startDate, endDate }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/reports/clinician-hours`,
        { startDate, endDate },
        config
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchClientDropoffReport = createAsyncThunk(
  "reports/fetchClientDropoffReport",
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(
        `${baseURL}/api/reports/client-dropoff`,
        config
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchActiveClientsReport = createAsyncThunk(
  "reports/fetchActiveClientsReport",
  async ({ daysBack }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      // If "All Time" is selected, don't send daysBack in the request
      const payload = daysBack === -1 ? {} : { daysBack };

      const response = await axios.post(
        `${baseURL}/api/reports/active-clients`,
        payload,
        config
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchRecallClientsReport = createAsyncThunk(
  "reports/fetchRecallClientsReport",
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.get(
        `${baseURL}/api/reports/recall-clients`,
        config
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchClientHistoryReport = createAsyncThunk(
  "reports/fetchClientHistoryReport",
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/reports/client-history`,
        {},
        config
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching client history report"
      );
    }
  }
);

export const fetchInvoiceReport = createAsyncThunk(
  "reports/fetchInvoiceReport",
  async ({ startDate, endDate }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/reports/invoice-report`,
        { startDate, endDate },
        config
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Fetch invoices for a clinic within a date range
export const fetchClinicInvoices = createAsyncThunk(
  "reports/fetchClinicInvoices",
  async ({ startDate, endDate, clinicId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/reports/invoice-report`,
        { startDate, endDate, clinicId },
        config
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchClinicianAvailabilityReport = createAsyncThunk(
  "reports/fetchClinicianAvailabilityReport",
  async ({ startDate, endDate }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/reports/clinician-availability`,
        { startDate, endDate },
        config
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchClinicianScheduleReport = createAsyncThunk(
  "reports/fetchClinicianScheduleReport",
  async ({ startDate, endDate, clinicianId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/reports/clinician-schedule`,
        { startDate, endDate, clinicianId },
        config
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchQuickBooksInvoicesReport = createAsyncThunk(
  "reports/fetchQuickBooksInvoicesReport",
  async ({ statusFilter }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/reports/quickbooks-invoices`,
        { statusFilter },
        config
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchQuickBooksPaymentsReport = createAsyncThunk(
  "reports/fetchQuickBooksPaymentsReport",
  async ({ statusFilter }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/reports/quickbooks-payments`,
        { statusFilter },
        config
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchQuickBooksAuditReport = createAsyncThunk(
  "reports/fetchQuickBooksAuditReport",
  async ({ startDate, endDate, clinicId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/reports/quickbooks-audit`,
        { startDate, endDate, clinicId },
        config
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchClinicPerformanceReport = createAsyncThunk(
  "reports/fetchClinicPerformanceReport",
  async ({ startDate, endDate, clinicId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/reports/clinic-performance`,
        { startDate, endDate, clinicId },
        config
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchBlockTherapyReport = createAsyncThunk(
  "reports/fetchBlockTherapyReport",
  async ({ clinicId, clinicianId, statusFilter }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/reports/block-therapy`,
        { clinicId, clinicianId, statusFilter },
        config
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchClientJourneyProgressReport = createAsyncThunk(
  "reports/fetchClientJourneyProgressReport",
  async ({ journeyTemplateId, clinicId, clinicianId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/reports/client-journey-progress`,
        { journeyTemplateId, clinicId, clinicianId },
        config
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchClinicianClientsReport = createAsyncThunk(
  "reports/fetchClinicianClientsReport",
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/reports/clinician-clients`,
        {},
        config
      );

      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchOrphanedUploadsReport = createAsyncThunk(
  "reports/fetchOrphanedUploadsReport",
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      console.log(`[Orphaned Uploads Thunk] Fetching orphaned uploads report`);
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/reports/orphaned-uploads`,
        {},
        config
      );

      console.log(`[Orphaned Uploads Thunk] Report fetched successfully: ${response.data.stats.orphanedCount} orphaned files found`);
      return response.data;
    } catch (error) {
      console.error(`[Orphaned Uploads Thunk] Error fetching report:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchPayrollSummaryReport = createAsyncThunk(
  "reports/fetchPayrollSummaryReport",
  async ({ startDate, endDate, clinics, cohort, statuses, clinicianIds }, { rejectWithValue, getState, dispatch }) => {
    try {
      console.log(`[Payroll Summary Thunk] Fetching payroll summary report for ${startDate} to ${endDate}`);
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/reports/payroll/summary`,
        { startDate, endDate, clinics, cohort, statuses, clinicianIds },
        config
      );

      console.log(`[Payroll Summary Thunk] Report fetched successfully`);
      return response.data;
    } catch (error) {
      console.error(`[Payroll Summary Thunk] Error fetching report:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchPayrollAuditReport = createAsyncThunk(
  "reports/fetchPayrollAuditReport",
  async ({ startDate, endDate, clinics, clinicianIds, showOnlyMissing }, { rejectWithValue, getState, dispatch }) => {
    try {
      console.log(`[Payroll Audit Thunk] Fetching payroll audit report for ${startDate} to ${endDate}`);
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/reports/payroll/audit`,
        { startDate, endDate, clinics, clinicianIds, showOnlyMissing },
        config
      );

      console.log(`[Payroll Audit Thunk] Audit fetched successfully - Missing: ${response.data?.data?.summary?.appointmentsMissingPayroll || 0}`);
      return response.data;
    } catch (error) {
      console.error(`[Payroll Audit Thunk] Error fetching audit:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

export const fetchCancellationTrackingReport = createAsyncThunk(
  "reports/fetchCancellationTrackingReport",
  async ({ startDate, endDate, clinicianId }, { rejectWithValue, getState, dispatch }) => {
    try {
      console.log(`[Cancellation Tracking Thunk] Fetching report for ${startDate} to ${endDate}, clinician: ${clinicianId || 'all'}`);
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      const response = await axios.post(
        `${baseURL}/api/reports/cancellation-tracking`,
        { startDate, endDate, clinicianId },
        config
      );

      console.log(`[Cancellation Tracking Thunk] Report fetched successfully - Total: ${response.data?.data?.summary?.totalCancellations + response.data?.data?.summary?.totalNoShows || 0}`);
      return response.data;
    } catch (error) {
      console.error(`[Cancellation Tracking Thunk] Error fetching report:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);
